/**
 * Freeze background page scroll while a modal/RFI overlay is open.
 * Overflow-only locks fail on iOS; treating #_modal as "inside" lets the
 * backdrop scroll the page because the overlay is portaled into #_modal.
 *
 * Do not use body { position: fixed; top: -scrollY }. Sticky header / filter
 * (e.g. /programs) unstick and jump to their in-flow positions, which sit
 * above the viewport after the user has scrolled.
 *
 * overflow:hidden on html/body still makes those nodes the sticky containing
 * block, so #header_new, the /programs filters, and the program-page sticky
 * tabs bar ([data-sticky-tabs]) all drop out of view. Pin visible sticky
 * chrome to their current rects (CSS on html, so re-renders cannot wipe
 * inline position) *before* overflow.
 */

export type PageScrollLockSnapshot = {
  htmlOverflow: string;
  bodyOverflow: string;
  bodyPosition: string;
  bodyTop: string;
  bodyLeft: string;
  bodyRight: string;
  bodyWidth: string;
  bodyPaddingRight: string;
  chromePlaceholders: HTMLElement[];
  pinnedHeader: boolean;
  pinnedFilters: boolean;
  pinnedTabs: boolean;
};

const DIALOG_SELECTOR = "#close, [data-modal-dialog], [role='dialog']";

const HEADER_VARS = [
  "--sl-header-top",
  "--sl-header-left",
  "--sl-header-width",
] as const;
const FILTER_VARS = [
  "--sl-filters-top",
  "--sl-filters-left",
  "--sl-filters-width",
] as const;
const TABS_VARS = [
  "--sl-tabs-top",
  "--sl-tabs-left",
  "--sl-tabs-width",
] as const;

export function isEventInsideModalDialog(target: EventTarget | null): boolean {
  const node = target as Node | null;
  if (!node) return false;
  if (typeof (node as Element).closest === "function") {
    return Boolean((node as Element).closest(DIALOG_SELECTOR));
  }
  return Boolean(document.getElementById("close")?.contains(node));
}

function makeFlowPlaceholder(rect: DOMRect): HTMLElement {
  const placeholder = document.createElement("div");
  placeholder.setAttribute("data-scroll-lock-chrome-spacer", "");
  placeholder.setAttribute("aria-hidden", "true");
  placeholder.style.width = `${rect.width}px`;
  placeholder.style.height = `${rect.height}px`;
  placeholder.style.flexShrink = "0";
  placeholder.style.pointerEvents = "none";
  return placeholder;
}

function pinStickyChrome(root: HTMLElement): {
  placeholders: HTMLElement[];
  pinnedHeader: boolean;
  pinnedFilters: boolean;
  pinnedTabs: boolean;
} {
  const placeholders: HTMLElement[] = [];
  let pinnedHeader = false;
  let pinnedFilters = false;
  let pinnedTabs = false;

  const header = document.getElementById("header_new");
  if (header) {
    const rect = header.getBoundingClientRect();
    const visible =
      rect.height > 1 &&
      rect.width > 1 &&
      rect.bottom > 0 &&
      rect.top < window.innerHeight;
    if (visible) {
      root.style.setProperty("--sl-header-top", `${rect.top}px`);
      root.style.setProperty("--sl-header-left", `${rect.left}px`);
      root.style.setProperty("--sl-header-width", `${rect.width}px`);
      root.classList.add("modal-scroll-lock--header");
      pinnedHeader = true;
      const placeholder = makeFlowPlaceholder(rect);
      header.parentNode?.insertBefore(placeholder, header);
      placeholders.push(placeholder);
    }
  }

  const filters = document.querySelector(
    "[data-program-filters]",
  ) as HTMLElement | null;
  if (filters) {
    const cs = getComputedStyle(filters);
    const rect = filters.getBoundingClientRect();
    const visible =
      cs.display !== "none" &&
      rect.height > 1 &&
      rect.width > 1 &&
      rect.bottom > 0 &&
      rect.top < window.innerHeight;
    if (visible) {
      root.style.setProperty("--sl-filters-top", `${rect.top}px`);
      root.style.setProperty("--sl-filters-left", `${rect.left}px`);
      root.style.setProperty("--sl-filters-width", `${rect.width}px`);
      root.classList.add("modal-scroll-lock--filters");
      pinnedFilters = true;
      const placeholder = makeFlowPlaceholder(rect);
      filters.parentNode?.insertBefore(placeholder, filters);
      placeholders.push(placeholder);
    }
  }

  const tabs = document.querySelector(
    "[data-sticky-tabs]",
  ) as HTMLElement | null;
  if (tabs) {
    const cs = getComputedStyle(tabs);
    const rect = tabs.getBoundingClientRect();
    const visible =
      cs.display !== "none" &&
      rect.height > 1 &&
      rect.width > 1 &&
      rect.bottom > 0 &&
      rect.top < window.innerHeight;
    if (visible) {
      root.style.setProperty("--sl-tabs-top", `${rect.top}px`);
      root.style.setProperty("--sl-tabs-left", `${rect.left}px`);
      root.style.setProperty("--sl-tabs-width", `${rect.width}px`);
      root.classList.add("modal-scroll-lock--tabs");
      pinnedTabs = true;
      const placeholder = makeFlowPlaceholder(rect);
      tabs.parentNode?.insertBefore(placeholder, tabs);
      placeholders.push(placeholder);
    }
  }

  return { placeholders, pinnedHeader, pinnedFilters, pinnedTabs };
}

function unpinStickyChrome(
  root: HTMLElement,
  snapshot: PageScrollLockSnapshot,
): void {
  root.classList.remove(
    "modal-scroll-lock--header",
    "modal-scroll-lock--filters",
    "modal-scroll-lock--tabs",
  );
  for (const name of HEADER_VARS) root.style.removeProperty(name);
  for (const name of FILTER_VARS) root.style.removeProperty(name);
  for (const name of TABS_VARS) root.style.removeProperty(name);
  for (const placeholder of snapshot.chromePlaceholders) {
    placeholder.remove();
  }
}

export function lockPageScroll(scrollY: number): PageScrollLockSnapshot {
  const root = document.documentElement;
  const body = document.body;
  const scrollbarWidth = window.innerWidth - root.clientWidth;

  const { placeholders, pinnedHeader, pinnedFilters, pinnedTabs } =
    pinStickyChrome(root);

  const snapshot: PageScrollLockSnapshot = {
    htmlOverflow: root.style.overflow,
    bodyOverflow: body.style.overflow,
    bodyPosition: body.style.position,
    bodyTop: body.style.top,
    bodyLeft: body.style.left,
    bodyRight: body.style.right,
    bodyWidth: body.style.width,
    bodyPaddingRight: body.style.paddingRight,
    chromePlaceholders: placeholders,
    pinnedHeader,
    pinnedFilters,
    pinnedTabs,
  };

  // Pin via CSS (html.modal-scroll-lock + --header/--filters) before overflow
  // so html/body overflow cannot unstick chrome for even one frame.
  root.classList.add("modal-scroll-lock");
  root.style.overflow = "hidden";
  body.style.overflow = "hidden";
  if (scrollbarWidth > 0) {
    body.style.paddingRight = `${scrollbarWidth}px`;
  }

  void scrollY;

  return snapshot;
}

export function unlockPageScroll(
  snapshot: PageScrollLockSnapshot,
  scrollY: number,
): void {
  const root = document.documentElement;
  const body = document.body;

  unpinStickyChrome(root, snapshot);

  root.style.overflow = snapshot.htmlOverflow;
  body.style.overflow = snapshot.bodyOverflow;
  body.style.position = snapshot.bodyPosition;
  body.style.top = snapshot.bodyTop;
  body.style.left = snapshot.bodyLeft;
  body.style.right = snapshot.bodyRight;
  body.style.width = snapshot.bodyWidth;
  body.style.paddingRight = snapshot.bodyPaddingRight;
  root.classList.remove("modal-scroll-lock");

  window.scrollTo({
    top: scrollY,
    left: 0,
    behavior: "instant" as ScrollBehavior,
  });
}
